// updated IvrStatusAdminPage.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Heading,
  Spinner,
  Collapse,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Button,
  Badge,
  Stack,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Input,
  IconButton,
  Tooltip,
  HStack,
  VStack,
  Text,
  Flex,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Divider,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Container,
  useColorModeValue,
  SimpleGrid,
  Avatar,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import {
  MdContentCopy,
  MdSearch,
  MdFilterList,
  MdRefresh,
  MdExpandMore,
  MdExpandLess,
  MdPersonOutline,
  MdLocalHospital,
  MdMedicalServices,
  MdCalendarToday,
  MdAssignment,
  MdFax,
  MdOpenInNew,
} from "react-icons/md";
import { useCognito } from "../utils/useCognito";
import { formatDateTime } from "../utils/dateUtils";
import FaxModal from "../components/FaxModal";
import { useNavigate } from "react-router-dom";

const IVR_STATUSES = [
  "submitted",
  "in progress",
  "signed",
  "awaiting documents",
  "approved",
  "denied",
  "complete",
];

export default function IvrStatusAdminPage() {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const [grouped, setGrouped] = useState({});
  const [expanded, setExpanded] = useState({});
  const [faxModalOpen, setFaxModalOpen] = useState(false);
  const [faxStatusMap, setFaxStatusMap] = useState({});
  const [selectedIvr, setSelectedIvr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusChange, setStatusChange] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState({});
  const [filterId, setFilterId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [allIvrs, setAllIvrs] = useState([]); // Store flattened list for chronological view
  const cancelRef = React.useRef();
  const navigate = useNavigate();

  const isTestMode = new URLSearchParams(window.location.search).get("test");

  // Theme colors
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const facilityBg = useColorModeValue("gray.50", "gray.700");
  const facilityHoverBg = useColorModeValue("gray.100", "gray.600");
  const collapseBg = useColorModeValue("white", "gray.800");
  const inputBg = useColorModeValue("white", "gray.700");

  // Status color mapping for modern UI
  const getStatusColor = (status) => {
    const statusColors = {
      submitted: "blue",
      "in progress": "orange",
      signed: "purple",
      "awaiting documents": "yellow",
      approved: "green",
      denied: "red",
      completed: "teal",
    };
    return statusColors[status] || "gray";
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    const total = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);
    const inProgress = (grouped["in progress"] || []).length;
    const completed = (grouped["completed"] || []).length;
    const pending =
      (grouped["submitted"] || []).length +
      (grouped["awaiting documents"] || []).length;
    return { total, inProgress, completed, pending };
  }, [grouped]);

  useEffect(() => {
    const stored = localStorage.getItem("faxStatusMap");
    if (stored) setFaxStatusMap(JSON.parse(stored));
  }, []);

  useEffect(() => {
    localStorage.setItem("faxStatusMap", JSON.stringify(faxStatusMap));
  }, [faxStatusMap]);

  const fetchIvrs = useCallback(
    async (showRefreshIndicator = false) => {
      try {
        if (showRefreshIndicator) setRefreshing(true);
        else setLoading(true);

        const token = getIdToken();

        const res = await fetch(
          "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllIvrs",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ test: isTestMode === "true" }),
          }
        );

        const data = await res.json();
        const parsed = JSON.parse(data.body);
        setGrouped(parsed.grouped || {});

        // Flatten and sort all IVRs by date for chronological view
        const flatIvrs = Object.values(parsed.grouped || {})
          .flat()
          .sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt || 0) -
              new Date(a.updatedAt || a.createdAt || 0)
          );
        setAllIvrs(flatIvrs);

        if (showRefreshIndicator) {
          toast({
            title: "Data refreshed",
            status: "success",
            duration: 2000,
          });
        }
      } catch (err) {
        console.error("Failed to fetch IVRs:", err);
        toast({
          title: "Failed to fetch IVRs",
          description: err.message,
          status: "error",
          duration: 5000,
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getIdToken, isTestMode, toast]
  );

  useEffect(() => {
    fetchIvrs();
  }, [fetchIvrs]);

  const toggleExpand = (status, facilityName) => {
    const key = `${status}:${facilityName}`;
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFaxData = (ivr) => {
    toast({
      title: "Fax modal coming soon",
      description: `Pretend we're faxing IVR ${ivr.ivrId}`,
      status: "info",
      duration: 2500,
      isClosable: true,
    });
  };

  const handleConfirmStatusChange = async () => {
    const { ivrId, newStatus, prevStatus } = statusChange;
    setDialogOpen(false);

    try {
      const token = getIdToken();

      const res = await fetch(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateIvrStatus",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ivrId, newStatus }),
        }
      );

      if (!res.ok) throw new Error("Non-200 response");

      toast({
        title: `IVR ${ivrId} updated to ${newStatus}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setStatusOverrides((prev) => ({ ...prev, [ivrId]: newStatus }));
    } catch (err) {
      console.error("Update failed:", err);
      setStatusOverrides((prev) => ({ ...prev, [ivrId]: prevStatus }));
      toast({
        title: "Status update failed",
        description: `Could not update IVR ${ivrId}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setStatusChange(null);
    }
  };

  const renderWounds = (ivr) => {
    const wounds = ivr.wounds || [];
    console.log("Rendering wounds for IVR:", ivr.ivrId, "Wounds:", wounds);

    return (
      <Table size="xs" mt={2} variant="simple">
        <Thead>
          <Tr>
            <Th>Dimensions</Th>
            <Th>Wound Size</Th>
            <Th>Application Date</Th>
          </Tr>
        </Thead>
        <Tbody>
          {wounds.flatMap((w, i) => {
            const grafts = w.graftsUsed?.length ? w.graftsUsed : [null];

            return grafts.map((g, j) => {
              const woundLength = g?.woundLength || w.woundLength || "-";
              const woundWidth = g?.woundWidth || w.woundWidth || "-";
              const woundDepth = g?.woundDepth || w.woundDepth || "-";

              const size = `${woundLength} x ${woundWidth} x ${woundDepth}`;
              const woundSize =
                parseFloat(woundLength) && parseFloat(woundWidth)
                  ? (parseFloat(woundLength) * parseFloat(woundWidth)).toFixed(2)
                  : "-";

              return (
                <Tr key={`${i}-${j}`}>
                  <Td>{size}</Td>
                  <Td>{woundSize}</Td>
                  <Td>{w.applicationDate || "-"}</Td>
                </Tr>
              );
            });
          })}
        </Tbody>
      </Table>
    );
  };

  // Filter IVRs based on search and status
  const filteredData = React.useMemo(() => {
    return IVR_STATUSES.reduce((acc, status) => {
      if (selectedStatus !== "all" && selectedStatus !== status) return acc;

      const ivrs = (grouped[status] || []).filter(
        (ivr) =>
          !filterId ||
          ivr.ivrId.toLowerCase().includes(filterId.toLowerCase()) ||
          ivr.patient?.name?.toLowerCase().includes(filterId.toLowerCase()) ||
          ivr.physician?.name?.toLowerCase().includes(filterId.toLowerCase()) ||
          ivr.facility?.name?.toLowerCase().includes(filterId.toLowerCase())
      );

      if (ivrs.length > 0) {
        acc[status] = ivrs;
      }
      return acc;
    }, {});
  }, [grouped, filterId, selectedStatus]);

  // Filter IVRs for chronological view
  const filteredChronologicalIvrs = React.useMemo(() => {
    return allIvrs.filter((ivr) => {
      const matchesSearch =
        !filterId ||
        ivr.ivrId.toLowerCase().includes(filterId.toLowerCase()) ||
        ivr.patient?.name?.toLowerCase().includes(filterId.toLowerCase()) ||
        ivr.physician?.name?.toLowerCase().includes(filterId.toLowerCase()) ||
        ivr.facility?.name?.toLowerCase().includes(filterId.toLowerCase());

      const matchesStatus = selectedStatus === "all" || ivr.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [allIvrs, filterId, selectedStatus]);

  // Keyboard shortcut: Alt+R to refresh
  const lastShortcutRef = useRef(0);
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const typeable =
        tag === "input" ||
        tag === "textarea" ||
        el.isContentEditable ||
        (tag === "div" && el.getAttribute("role") === "textbox");
      return typeable;
    };

    const onKeyDown = (e) => {
      // Alt+R (case-insensitive)
      if (e.altKey && (e.key === "r" || e.key === "R")) {
        // Avoid stealing while typing into inputs/textareas/editables
        if (isTypingTarget(e.target)) return;

        // Stop browser defaults and other handlers
        e.preventDefault();
        e.stopPropagation();

        // Debounce to avoid repeat-fire when held
        const now = Date.now();
        if (now - lastShortcutRef.current < 800) return;
        lastShortcutRef.current = now;

        fetchIvrs(true);
      }
    };

    // Use capture phase so we pre-empt app-level handlers; helps dodge browser quirks.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [fetchIvrs]);

  // Render chronological view
  const renderChronologicalView = () => {
    if (filteredChronologicalIvrs.length === 0) {
      return (
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" color="gray.500">
                No IVRs found matching your criteria
              </Text>
              <Text fontSize="sm" color="gray.400">
                Try adjusting your search terms or status filter
              </Text>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    return (
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader pb={2}>
          <Text fontSize="lg" fontWeight="semibold">
            All IVRs – Chronological ({filteredChronologicalIvrs.length})
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            {filteredChronologicalIvrs.map((ivr) => {
              const wounds = ivr.wounds || [];
              return (
                <Card key={ivr.ivrId} variant="outline" size="sm" borderColor={borderColor}>
                  <CardBody py={2} px={3}>
                    <Grid
                      templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 220px" }}
                      gap={3}
                      alignItems="start"
                    >
                      {/* Left cluster: ID + patient/provider/facility + updated */}
                      <VStack spacing={1} align="start" fontSize="xs" minW={0}>
                        <HStack spacing={2} minW={0}>
                          <Text fontWeight="bold" color="teal.600" fontSize="sm" noOfLines={1} title={ivr.ivrId}>
                            {ivr.ivrId}
                          </Text>
                          <Tooltip label="Copy IVR ID">
                            <IconButton
                              icon={<MdContentCopy />}
                              size="xs"
                              variant="ghost"
                              aria-label="Copy IVR ID"
                              onClick={() => {
                                navigator.clipboard.writeText(ivr.ivrId);
                                toast({ title: "IVR ID copied", status: "success", duration: 1200 });
                              }}
                            />
                          </Tooltip>
                        </HStack>
                        <Text noOfLines={1} color="gray.700" title={ivr.patient?.name || 'N/A'}>
                          <Text as="span" fontWeight="semibold">Patient:</Text> {ivr.patient?.name || 'N/A'}
                        </Text>
                        <Text noOfLines={1} color="gray.700" title={ivr.physician?.name || 'N/A'}>
                          <Text as="span" fontWeight="semibold">Provider:</Text> {ivr.physician?.name || 'N/A'}
                        </Text>
                        <Text noOfLines={1} color="gray.600" title={ivr.facility?.name || 'Unknown Facility'}>
                          <Text as="span" fontWeight="semibold">Facility:</Text> {ivr.facility?.name || 'Unknown Facility'}
                        </Text>
                        <HStack spacing={1} fontSize="xs" color="gray.500">
                          <MdCalendarToday />
                          <Text>{formatDateTime(ivr.updatedAt)}</Text>
                        </HStack>
                      </VStack>

                      {/* Status + Fax + Status change */}
                      <VStack spacing={1} align="start" fontSize="xs" minW={0}>
                        <Badge
                          colorScheme={getStatusColor(ivr.status)}
                          variant="solid"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                        >
                          {ivr.status}
                        </Badge>
                        {faxStatusMap[ivr.ivrId] && (
                          <Tooltip label={`Faxed on ${new Date(faxStatusMap[ivr.ivrId].sentAt).toLocaleDateString()}`}>
                            <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2}>
                              <MdFax style={{ marginRight: 4 }} /> Faxed
                            </Badge>
                          </Tooltip>
                        )}
                        <Select
                          size="xs"
                          value={statusOverrides[ivr.ivrId] || ivr.status}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            setStatusOverrides((prev) => ({ ...prev, [ivr.ivrId]: newStatus }));
                            setStatusChange({ ivrId: ivr.ivrId, newStatus, prevStatus: ivr.status });
                            setDialogOpen(true);
                          }}
                          maxW="160px"
                        >
                          {IVR_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </Select>
                      </VStack>

                      {/* Wounds */}
                      <VStack spacing={1} align="start" fontSize="xs" minW={0}>
                        {wounds.length ? (
                          <>
                            <Text fontWeight="semibold" fontSize="xs">
                              Wounds ({wounds.length})
                            </Text>
                            {wounds.slice(0, 2).map((w, i) => (
                              <Text key={i} color="gray.600" noOfLines={1}>
                                {w.woundLength}×{w.woundWidth}×{w.woundDepth || 0} cm
                              </Text>
                            ))}
                            {wounds.length > 2 && (
                              <Text color="gray.500">+{wounds.length - 2} more</Text>
                            )}
                          </>
                        ) : (
                          <Text color="gray.500">No wounds</Text>
                        )}
                      </VStack>

                      {/* Actions */}
                      <VStack spacing={1} align="stretch" fontSize="xs">
                        <HStack spacing={1} wrap="wrap">
                          <Button
                            size="xs"
                            colorScheme="green"
                            onClick={() => navigate(`/admin/create-order/${ivr.ivrId}/${ivr.userId}${isTestMode === 'true' ? '?test=true' : ''}`)}
                          >
                            Create Order
                          </Button>
                          {(() => {
                            const preSubmittedStatuses = ['created', 'in progress', 'signed', 'awaiting documents'];
                            const isPreSubmitted = preSubmittedStatuses.includes(ivr.status);
                            const hasFaxRecord = !!faxStatusMap[ivr.ivrId];
                            const tooltipLabel = isPreSubmitted
                              ? 'The user has not signed & submitted this IVR yet.'
                              : hasFaxRecord
                                ? 'IVR has already been faxed. Open fax menu.'
                                : 'Open fax menu';
                            return (
                              <Tooltip label={tooltipLabel} isDisabled={!isPreSubmitted && !hasFaxRecord}>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="outline"
                                  leftIcon={<MdFax />}
                                  onClick={() => { if (!isPreSubmitted) { setSelectedIvr(ivr); setFaxModalOpen(true); } }}
                                  isDisabled={isPreSubmitted}
                                >
                                  Fax
                                </Button>
                              </Tooltip>
                            );
                          })()}
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<MdOpenInNew />}
                            onClick={() => window.open(`https://www.dropbox.com/home/IVR%20Uploads/IVRs/${ivr.ivrId}/files`, '_blank')}
                          >
                            Files
                          </Button>
                        </HStack>
                      </VStack>
                    </Grid>
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="7xl" py={8}>
        {/* Header Section */}
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <VStack align="start" spacing={1}>
              <Heading size="xl" color="teal.600">
                IVR Management Dashboard
              </Heading>
              <Text color="gray.600" fontSize="lg">
                Monitor and manage Internal Verification Reports
                {isTestMode && (
                  <Badge ml={2} colorScheme="orange" variant="subtle">
                    Test Mode
                  </Badge>
                )}
              </Text>
            </VStack>

            <Tooltip label="Shortcut: Alt + R">
              <Button
                leftIcon={<MdRefresh />}
                onClick={() => fetchIvrs(true)}
                isLoading={refreshing}
                loadingText="Refreshing"
                colorScheme="teal"
                variant="outline"
                size="lg"
                _hover={{ transform: "scale(1)" }}
              >
                Refresh Data
              </Button>
            </Tooltip>
            <Button
              size="lg"
              variant="ghost"
              onClick={() => navigate('/tools/create-ivr')}
            >
              New IVR
            </Button>
          </Flex>

          {/* Statistics Cards */}
          {!loading && (
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <Stat>
                    <StatLabel color="gray.600">Total IVRs</StatLabel>
                    <StatNumber fontSize="2xl" color="blue.500">
                      {stats.total}
                    </StatNumber>
                    <StatHelpText>All records</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <Stat>
                    <StatLabel color="gray.600">In Progress</StatLabel>
                    <StatNumber fontSize="2xl" color="orange.500">
                      {stats.inProgress}
                    </StatNumber>
                    <StatHelpText>Active cases</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <Stat>
                    <StatLabel color="gray.600">Completed</StatLabel>
                    <StatNumber fontSize="2xl" color="green.500">
                      {stats.completed}
                    </StatNumber>
                    <StatHelpText>Finished cases</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor}>
                <CardBody>
                  <Stat>
                    <StatLabel color="gray.600">Pending Action</StatLabel>
                    <StatNumber fontSize="2xl" color="yellow.500">
                      {stats.pending}
                    </StatNumber>
                    <StatHelpText>Needs attention</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>
          )}

          {/* Filters Section */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Flex align="center" gap={2}>
                <MdFilterList />
                <Text fontWeight="semibold">Filters & Search</Text>
              </Flex>
            </CardHeader>
            <CardBody>
              <Grid templateColumns={{ base: "1fr", md: "1fr 200px" }} gap={4}>
                <GridItem>
                  <InputGroup>
                    <InputLeftElement>
                      <MdSearch color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by IVR ID, patient, physician, or facility..."
                      value={filterId}
                      onChange={(e) => setFilterId(e.target.value)}
                      bg={inputBg}
                    />
                  </InputGroup>
                </GridItem>
                <GridItem>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    bg={inputBg}
                  >
                    <option value="all">All Statuses</option>
                    {IVR_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </Select>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Loading State */}
          {loading ? (
            <Flex justify="center" align="center" minH="300px">
              <VStack spacing={4}>
                <Spinner size="xl" color="teal.500" thickness="4px" />
                <Text color="gray.600">Loading IVR data...</Text>
              </VStack>
            </Flex>
          ) : (
            /* Tabbed Views */
            <Tabs variant="enclosed" colorScheme="teal">
              <TabList>
                <Tab>By Status</Tab>
                <Tab>Chronological</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  {/* IVR Status Groups */}
                  <VStack spacing={6} align="stretch">
                    {Object.entries(filteredData).map(([status, ivrs]) => {
                      const facilities = {};
                      for (const ivr of ivrs) {
                        const name = ivr.facility?.name || "Unknown Facility";
                        if (!facilities[name]) facilities[name] = [];
                        facilities[name].push(ivr);
                      }

                      return (
                        <Card
                          key={status}
                          bg={cardBg}
                          borderColor={borderColor}
                          shadow="md"
                        >
                          <CardHeader>
                            <Flex justify="space-between" align="center">
                              <HStack spacing={3}>
                                <Badge
                                  colorScheme={getStatusColor(status)}
                                  variant="solid"
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                  fontSize="sm"
                                >
                                  {status.toUpperCase()}
                                </Badge>
                                <Text fontSize="lg" fontWeight="semibold">
                                  {ivrs.length} {ivrs.length === 1 ? "IVR" : "IVRs"}
                                </Text>
                              </HStack>
                            </Flex>
                          </CardHeader>
                          <CardBody>
                            <VStack spacing={4} align="stretch">
                              {Object.entries(facilities).map(
                                ([facilityName, facilityIvrs]) => {
                                  const key = `${status}:${facilityName}`;
                                  const isExpanded = expanded[key];

                                  return (
                                    <Box key={key}>
                                      <Flex
                                        justify="space-between"
                                        align="center"
                                        p={4}
                                        bg={facilityBg}
                                        borderRadius="lg"
                                        cursor="pointer"
                                        onClick={() =>
                                          toggleExpand(status, facilityName)
                                        }
                                        _hover={{ bg: facilityHoverBg }}
                                        transition="all 0.2s"
                                      >
                                        <HStack spacing={3}>
                                          <MdLocalHospital color="gray.600" />
                                          <Text fontWeight="medium" fontSize="md">
                                            {facilityName}
                                          </Text>
                                          <Badge variant="outline" colorScheme="gray">
                                            {facilityIvrs.length}{" "}
                                            {facilityIvrs.length === 1
                                              ? "case"
                                              : "cases"}
                                          </Badge>
                                        </HStack>
                                        <IconButton
                                          icon={
                                            isExpanded ? (
                                              <MdExpandLess />
                                            ) : (
                                              <MdExpandMore />
                                            )
                                          }
                                          variant="ghost"
                                          size="sm"
                                          aria-label={
                                            isExpanded ? "Collapse" : "Expand"
                                          }
                                        />
                                      </Flex>

                                      <Collapse in={isExpanded}>
                                        <Box
                                          mt={4}
                                          p={4}
                                          bg={collapseBg}
                                          borderRadius="md"
                                          border="1px solid"
                                          borderColor={borderColor}
                                        >
                                          <VStack spacing={4} align="stretch">
                                            {facilityIvrs.map((ivr) => (
                                              <Card
                                                key={ivr.ivrId}
                                                variant="outline"
                                                size="sm"
                                              >
                                                <CardBody>
                                                  <Grid
                                                    templateColumns={{
                                                      base: "1fr",
                                                      lg: "2fr 1fr 1fr 200px",
                                                    }}
                                                    gap={4}
                                                    alignItems="start"
                                                  >
                                                    {/* IVR Details Column */}
                                                    <VStack align="start" spacing={2}>
                                                      <HStack spacing={2}>
                                                        <MdAssignment />
                                                        <Text
                                                          fontWeight="bold"
                                                          color="teal.600"
                                                        >
                                                          {ivr.ivrId}
                                                        </Text>
                                                        <Tooltip label="Copy IVR ID">
                                                          <IconButton
                                                            icon={<MdContentCopy />}
                                                            size="xs"
                                                            variant="ghost"
                                                            onClick={() => {
                                                              navigator.clipboard.writeText(
                                                                ivr.ivrId
                                                              );
                                                              toast({
                                                                title: "IVR ID copied!",
                                                                status: "info",
                                                                duration: 1500,
                                                              });
                                                            }}
                                                          />
                                                        </Tooltip>
                                                      </HStack>

                                                      <HStack spacing={2}>
                                                        <MdPersonOutline />
                                                        <Text
                                                          fontSize="sm"
                                                          color="gray.600"
                                                        >
                                                          <Text
                                                            as="span"
                                                            fontWeight="medium"
                                                          >
                                                            Patient:
                                                          </Text>{" "}
                                                          {ivr.patient?.name || "N/A"}
                                                        </Text>
                                                      </HStack>

                                                      <HStack spacing={2}>
                                                        <MdMedicalServices />
                                                        <Text
                                                          fontSize="sm"
                                                          color="gray.600"
                                                        >
                                                          <Text
                                                            as="span"
                                                            fontWeight="medium"
                                                          >
                                                            Provider:
                                                          </Text>{" "}
                                                          {ivr.physician?.name || "N/A"}
                                                        </Text>
                                                      </HStack>
                                                    </VStack>

                                                    {/* Status & Update Info */}
                                                    <VStack align="start" spacing={2}>
                                                      <Badge
                                                        colorScheme={getStatusColor(
                                                          ivr.status
                                                        )}
                                                        variant="solid"
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                      >
                                                        {ivr.status}
                                                      </Badge>

                                                      <HStack spacing={2}>
                                                        <MdCalendarToday size="14px" />
                                                        <Text
                                                          fontSize="xs"
                                                          color="gray.500"
                                                        >
                                                          Updated:{" "}
                                                          {formatDateTime(ivr.updatedAt)}
                                                        </Text>
                                                      </HStack>

                                                      {faxStatusMap[ivr.ivrId] && (
                                                        <Tooltip
                                                          label={`Faxed on ${new Date(
                                                            faxStatusMap[ivr.ivrId].sentAt
                                                          ).toLocaleDateString()}`}
                                                        >
                                                          <Badge
                                                            colorScheme="blue"
                                                            variant="outline"
                                                            fontSize="xs"
                                                          >
                                                            <MdFax
                                                              style={{ marginRight: "4px" }}
                                                            />
                                                            Faxed
                                                          </Badge>
                                                        </Tooltip>
                                                      )}
                                                    </VStack>

                                                    {/* Wounds Info */}
                                                    <Box>
                                                      {ivr.wounds &&
                                                      ivr.wounds.length > 0 ? (
                                                        <VStack
                                                          align="start"
                                                          spacing={1}
                                                        >
                                                          <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color="gray.700"
                                                          >
                                                            Wounds ({ivr.wounds.length})
                                                          </Text>
                                                          {ivr.wounds
                                                            .slice(0, 2)
                                                            .map((wound, idx) => (
                                                              <Text
                                                                key={idx}
                                                                fontSize="xs"
                                                                color="gray.600"
                                                              >
                                                                {wound.woundLength}×
                                                                {wound.woundWidth}×
                                                                {wound.woundDepth || 0} cm
                                                              </Text>
                                                            ))}
                                                          {ivr.wounds.length > 2 && (
                                                            <Text
                                                              fontSize="xs"
                                                              color="gray.500"
                                                            >
                                                              +
                                                              {ivr.wounds.length - 2} more
                                                            </Text>
                                                          )}
                                                        </VStack>
                                                      ) : (
                                                        <Text
                                                          fontSize="sm"
                                                          color="gray.500"
                                                        >
                                                          No wounds recorded
                                                        </Text>
                                                      )}
                                                    </Box>

                                                    {/* Actions Column */}
                                                    <VStack spacing={2} align="stretch">
                                                      <Select
                                                        size="sm"
                                                        value={
                                                          statusOverrides[ivr.ivrId] ||
                                                          ivr.status
                                                        }
                                                        onChange={(e) => {
                                                          const newStatus = e.target.value;
                                                          setStatusOverrides((prev) => ({
                                                            ...prev,
                                                            [ivr.ivrId]: newStatus,
                                                          }));
                                                          setStatusChange({
                                                            ivrId: ivr.ivrId,
                                                            newStatus,
                                                            prevStatus: ivr.status,
                                                          });
                                                          setDialogOpen(true);
                                                        }}
                                                      >
                                                        {IVR_STATUSES.map((s) => (
                                                          <option key={s} value={s}>
                                                            {s.charAt(0).toUpperCase() +
                                                              s.slice(1)}
                                                          </option>
                                                        ))}
                                                      </Select>

                                                      <Wrap spacing={1}>
                                                        <WrapItem>
                                                          <Button
                                                            size="sm"
                                                            colorScheme="green"
                                                            variant="solid"
                                                            _hover={{
                                                              transform: "scale(1)",
                                                            }}
                                                            onClick={() =>
                                                              navigate(
                                                                `/admin/create-order/${ivr.ivrId}/${ivr.userId}${
                                                                  isTestMode === "true"
                                                                    ? "?test=true"
                                                                    : ""
                                                                }`
                                                              )
                                                            }
                                                          >
                                                            Create Order
                                                          </Button>
                                                        </WrapItem>

                                                        <WrapItem>
                                                          {(() => {
                                                            const preSubmittedStatuses = ['created', 'in progress', 'signed', 'awaiting documents'];
                                                            const isPreSubmitted = preSubmittedStatuses.includes(ivr.status);
                                                            const hasFaxRecord = !!faxStatusMap[ivr.ivrId];
                                                            const tooltipLabel = isPreSubmitted
                                                              ? 'The user has not signed & submitted this IVR yet.'
                                                              : hasFaxRecord
                                                                ? 'IVR has already been faxed. Open fax menu.'
                                                                : 'Open fax menu';
                                                            return (
                                                              <Tooltip label={tooltipLabel} isDisabled={!isPreSubmitted && !hasFaxRecord}>
                                                                <Button
                                                                  size="sm"
                                                                  colorScheme="blue"
                                                                  variant="outline"
                                                                  leftIcon={<MdFax />}
                                                                  _hover={{ transform: "scale(1)" }}
                                                                  onClick={() => { if (!isPreSubmitted) { setSelectedIvr(ivr); setFaxModalOpen(true); } }}
                                                                  isDisabled={isPreSubmitted}
                                                                >
                                                                  Fax
                                                                </Button>
                                                              </Tooltip>
                                                            );
                                                          })()}
                                                        </WrapItem>

                                                        <WrapItem>
                                                          <Button
                                                            size="sm"
                                                            variant="outline"
                                                            leftIcon={<MdOpenInNew />}
                                                            _hover={{
                                                              transform: "scale(1)",
                                                            }}
                                                            onClick={() =>
                                                              window.open(
                                                                `https://www.dropbox.com/home/IVR%20Uploads/IVRs/${ivr.ivrId}/files`,
                                                                "_blank"
                                                              )
                                                            }
                                                          >
                                                            Files
                                                          </Button>
                                                        </WrapItem>
                                                      </Wrap>
                                                    </VStack>
                                                  </Grid>
                                                </CardBody>
                                              </Card>
                                            ))}
                                          </VStack>
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                }
                              )}
                            </VStack>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </VStack>

                  {/* No Results Message for Status View */}
                  {Object.keys(filteredData).length === 0 && (
                    <Card bg={cardBg} borderColor={borderColor}>
                      <CardBody>
                        <VStack spacing={4} py={8}>
                          <Text fontSize="lg" color="gray.500">
                            No IVRs found matching your criteria
                          </Text>
                          <Text fontSize="sm" color="gray.400">
                            Try adjusting your search terms or status filter
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}
                </TabPanel>

                <TabPanel>{renderChronologicalView()}</TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </VStack>
      </Container>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setDialogOpen(false);
          setStatusChange(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Status Update
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to change the status of{" "}
              <strong>{statusChange?.ivrId}</strong> from{" "}
              <Badge colorScheme={getStatusColor(statusChange?.prevStatus)} mx={1}>
                {statusChange?.prevStatus}
              </Badge>
              to{" "}
              <Badge colorScheme={getStatusColor(statusChange?.newStatus)} mx={1}>
                {statusChange?.newStatus}
              </Badge>
              ?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  if (statusChange?.ivrId && statusChange?.prevStatus) {
                    setStatusOverrides((prev) => ({
                      ...prev,
                      [statusChange.ivrId]: statusChange.prevStatus,
                    }));
                  }
                  setDialogOpen(false);
                  setStatusChange(null);
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="teal" onClick={handleConfirmStatusChange} ml={3}>
                Confirm Change
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Fax Modal */}
      {faxModalOpen && selectedIvr && (
        <FaxModal
          isOpen={faxModalOpen}
          onClose={() => setFaxModalOpen(false)}
          ivrId={selectedIvr.ivrId}
          userId={selectedIvr.userId}
          onSuccess={({ tofaxname, tofaxno, sentAt }) => {
            setFaxStatusMap((prev) => ({
              ...prev,
              [selectedIvr.ivrId]: { tofaxname, tofaxno, sentAt },
            }));
          }}
        />
      )}
    </Box>
  );
}
